import { useEffect, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';

interface LegalKnowledge {
  id: number;
  title: string;
  code: string;
  type: 'law' | 'guideline' | 'policy';
  description: string;
  caseTypes: string[];
  uploadedAt: string;
  status: string;
}

const API_BASE = 'https://guardiancloudcare.com/backend/api';

const getCaseTypes = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string =>
        typeof item === 'string' &&
        item.trim() !== ''
    );
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed.filter(
          (item): item is string =>
            typeof item === 'string' &&
            item.trim() !== ''
        );
      }
    } catch {
      if (value.trim() !== '') {
        return [value.trim()];
      }
    }
  }

  return [];
};

const getLawIcon = (
  code: string,
  type: string
) => {
  const normalizedCode =
    code.toUpperCase();

  if (
    normalizedCode.includes('9208') ||
    normalizedCode.includes('10364')
  ) {
    return 'ri-shield-user-line';
  }

  if (
    normalizedCode.includes('7610') ||
    normalizedCode.includes('9231')
  ) {
    return 'ri-shield-check-line';
  }

  if (
    normalizedCode.includes('9262')
  ) {
    return 'ri-forbid-line';
  }

  if (
    normalizedCode.includes('9344')
  ) {
    return 'ri-scales-3-line';
  }

  if (
    type === 'guideline'
  ) {
    return 'ri-book-mark-line';
  }

  if (
    type === 'policy'
  ) {
    return 'ri-file-shield-2-line';
  }

  return 'ri-scales-3-line';
};

const getTypeColor = (
  type: string
) => {
  switch (type) {
    case 'guideline':
      return 'bg-teal-50 text-teal-600';

    case 'policy':
      return 'bg-amber-50 text-amber-600';

    default:
      return 'bg-yellow-50 text-orange-500';
  }
};

const getTypeBadge = (
  type: string
) => {
  switch (type) {
    case 'guideline':
      return 'Guideline';

    case 'policy':
      return 'Policy';

    default:
      return 'Law';
  }
};

const getCaseTypeColor = (
  caseType: string
) => {
  const normalized =
    caseType.toLowerCase();

  if (
    normalized.includes('sexual') ||
    normalized.includes('trafficking') ||
    normalized.includes('exploitation')
  ) {
    return 'bg-red-50 text-red-600 border-red-100';
  }

  if (
    normalized.includes('physical') ||
    normalized.includes('emotional') ||
    normalized.includes('abuse')
  ) {
    return 'bg-orange-50 text-orange-600 border-orange-100';
  }

  if (
    normalized.includes('labor')
  ) {
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

  if (
    normalized.includes('neglect') ||
    normalized.includes('abandonment')
  ) {
    return 'bg-purple-50 text-purple-600 border-purple-100';
  }

  return 'bg-slate-50 text-slate-600 border-slate-200';
};

export default function VLawPage() {
  const [laws, setLaws] = useState<LegalKnowledge[]>([]);
  const [openId, setOpenId] =
    useState<number | null>(null);
  const [activeFilter, setActiveFilter] =
    useState('All Laws');
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState('');

  const loadLegalKnowledge =
    async () => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(
          `${API_BASE}/get_legal_knowledge.php`
        );

        if (!response.ok) {
          throw new Error(
            `Unable to load legal knowledge. Server returned ${response.status}.`
          );
        }

        const result =
          await response.json();

        if (!result?.success) {
          throw new Error(
            result?.error ||
              result?.message ||
              'Failed to load legal knowledge.'
          );
        }

        const records =
          Array.isArray(result.data)
            ? result.data
            : [];

        const formattedRecords =
          records
            .map(
              (record: any) => {
                const id =
                  Number(record.id);

                if (
                  !Number.isInteger(id) ||
                  id <= 0
                ) {
                  return null;
                }

                const status =
                  String(
                    record.status || ''
                  ).toLowerCase();

                if (
                  status !== 'active'
                ) {
                  return null;
                }

                const legalType =
                  record.legal_type ===
                  'guideline'
                    ? 'guideline'
                    : record.legal_type ===
                      'policy'
                    ? 'policy'
                    : 'law';

                return {
                  id,
                  title:
                    String(
                      record.document_name ||
                        record.title ||
                        ''
                    ).trim(),
                  code:
                    String(
                      record.legal_code ||
                        record.code ||
                        ''
                    ).trim(),
                  type: legalType,
                  description:
                    String(
                      record.description ||
                        ''
                    ).trim(),
                  caseTypes:
                    getCaseTypes(
                      record.case_types
                    ),
                  uploadedAt:
                    record.uploaded_at
                      ? String(
                          record.uploaded_at
                        ).split(' ')[0]
                      : '',
                  status,
                };
              }
            )
            .filter(
              (
                record
              ): record is LegalKnowledge =>
                record !== null &&
                record.title !== ''
            );

        setLaws(
          formattedRecords
        );

        if (
          formattedRecords.length > 0
        ) {
          setOpenId(
            formattedRecords[0].id
          );
        } else {
          setOpenId(null);
        }
      } catch (err: any) {
        console.error(
          'Failed to load legal knowledge:',
          err
        );

        setLaws([]);

        setError(
          err?.message ||
            'Failed to load legal knowledge.'
        );
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    loadLegalKnowledge();
  }, []);

  const filters = [
    'All Laws',
    'Laws',
    'Guidelines',
    'Policies',
  ];

  const filteredLaws =
    activeFilter === 'All Laws'
      ? laws
      : laws.filter(
          law => {
            if (
              activeFilter ===
              'Laws'
            ) {
              return (
                law.type === 'law'
              );
            }

            if (
              activeFilter ===
              'Guidelines'
            ) {
              return (
                law.type ===
                'guideline'
              );
            }

            if (
              activeFilter ===
              'Policies'
            ) {
              return (
                law.type ===
                'policy'
              );
            }

            return true;
          }
        );

  const lawCount =
    laws.filter(
      law => law.type === 'law'
    ).length;

  const guidelineCount =
    laws.filter(
      law =>
        law.type === 'guideline'
    ).length;

  const policyCount =
    laws.filter(
      law => law.type === 'policy'
    ).length;

  return (
    <AppLayout
      title="Child Protection Laws"
      breadcrumb={[
        'Victim',
        'Law',
      ]}
    >
      <div className="max-w-5xl mx-auto space-y-4 sm:space-y-5 px-1 sm:px-0">

        <section className="bg-yellow-50 border border-yellow-100 rounded-2xl sm:rounded-3xl p-4 sm:p-5 flex gap-3 sm:gap-4">
          <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-white flex items-center justify-center flex-shrink-0">
            <i className="ri-scales-3-line text-orange-500 text-xl sm:text-2xl" />
          </div>

          <div className="flex-1">
            <h2 className="font-bold text-gray-900 text-sm sm:text-base">
              Know the Law, Protect Children
            </h2>

            <p className="text-xs sm:text-sm text-gray-600 mt-2 leading-relaxed">
              The Philippines has laws and policies that protect children from abuse, exploitation, neglect, trafficking, and other forms of harm. Review the legal information available in the system.
            </p>
          </div>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <i className="ri-scales-3-line text-red-500" />
              </div>

              <div>
                <p className="text-xs text-gray-400">
                  Laws
                </p>

                <p className="text-xl font-bold text-gray-900">
                  {lawCount}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <i className="ri-book-mark-line text-teal-500" />
              </div>

              <div>
                <p className="text-xs text-gray-400">
                  Guidelines
                </p>

                <p className="text-xl font-bold text-gray-900">
                  {guidelineCount}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <i className="ri-file-shield-2-line text-amber-500" />
              </div>

              <div>
                <p className="text-xs text-gray-400">
                  Policies
                </p>

                <p className="text-xl font-bold text-gray-900">
                  {policyCount}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {filters.map(
            filter => (
              <button
                key={filter}
                onClick={() => {
                  setActiveFilter(
                    filter
                  );
                  setOpenId(null);
                }}
                className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition ${
                  activeFilter ===
                  filter
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 border border-gray-100 hover:bg-orange-50'
                }`}
              >
                {filter}
              </button>
            )
          )}
        </div>

        {loading ? (
          <div className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-10 shadow-sm">
            <div className="flex flex-col items-center justify-center">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-orange-500 rounded-full animate-spin" />

              <p className="mt-4 text-sm text-gray-500">
                Loading legal information...
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <i className="ri-error-warning-line text-2xl text-red-500" />
            </div>

            <h3 className="mt-4 font-bold text-red-700">
              Unable to load legal information
            </h3>

            <p className="text-sm text-red-600 mt-2">
              {error}
            </p>

            <button
              type="button"
              onClick={loadLegalKnowledge}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition"
            >
              <i className="ri-refresh-line" />
              Try Again
            </button>
          </div>
        ) : filteredLaws.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {filteredLaws.map(
              law => {
                const isOpen =
                  openId === law.id;

                return (
                  <div
                    key={law.id}
                    className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenId(
                          isOpen
                            ? null
                            : law.id
                        )
                      }
                      className="w-full flex items-start gap-3 sm:gap-4 text-left"
                    >
                      <div
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${getTypeColor(
                          law.type
                        )}`}
                      >
                        <i
                          className={`${getLawIcon(
                            law.code,
                            law.type
                          )} text-lg sm:text-xl`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                          <span className="text-[10px] sm:text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded-md">
                            {law.code ||
                              getTypeBadge(
                                law.type
                              )}
                          </span>

                          <span className="text-[10px] sm:text-xs font-semibold text-gray-500 bg-gray-50 px-2 py-1 rounded-md">
                            {getTypeBadge(
                              law.type
                            )}
                          </span>

                          {law.uploadedAt && (
                            <span className="text-[10px] sm:text-xs text-gray-400">
                              Updated{' '}
                              {law.uploadedAt}
                            </span>
                          )}
                        </div>

                        <h3 className="font-bold text-gray-900 leading-snug text-sm sm:text-base">
                          {law.title}
                        </h3>
                      </div>

                      <i
                        className={`${
                          isOpen
                            ? 'ri-arrow-up-s-line'
                            : 'ri-arrow-down-s-line'
                        } text-gray-400 text-xl flex-shrink-0`}
                      />
                    </button>

                    {isOpen && (
                      <div className="pt-4 sm:pt-5 pl-0 sm:pl-16">
                        {law.description && (
                          <div>
                            <h4 className="text-[10px] sm:text-xs font-bold uppercase tracking-wide text-gray-800 mb-3">
                              Description
                            </h4>

                            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                              {law.description}
                            </p>
                          </div>
                        )}

                        {law.caseTypes.length >
                          0 && (
                          <div className="mt-5">
                            <h4 className="text-[10px] sm:text-xs font-bold uppercase tracking-wide text-gray-800 mb-3">
                              Related Case Types
                            </h4>

                            <div className="flex flex-wrap gap-2">
                              {law.caseTypes.map(
                                caseType => (
                                  <span
                                    key={
                                      caseType
                                    }
                                    className={`inline-flex items-center px-2.5 py-1.5 rounded-lg border text-xs font-medium ${getCaseTypeColor(
                                      caseType
                                    )}`}
                                  >
                                    {caseType}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {!law.description &&
                          law.caseTypes.length ===
                            0 && (
                            <div className="bg-gray-50 rounded-2xl p-4">
                              <p className="text-xs sm:text-sm text-gray-500">
                                No additional information is currently available for this legal document.
                              </p>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center shadow-sm">
            <i className="ri-file-search-line text-4xl text-gray-300" />

            <h3 className="font-semibold text-gray-700 mt-4">
              No legal information found
            </h3>

            <p className="text-gray-500 mt-2 text-sm">
              There are currently no active legal documents for this selection.
            </p>
          </div>
        )}

        <section className="bg-red-50 border border-red-200 rounded-2xl sm:rounded-3xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-fill text-red-500 text-xl" />
            </div>

            <div>
              <h3 className="font-bold text-red-600 text-sm sm:text-base">
                Witnessing a violation?
              </h3>

              <p className="text-xs sm:text-sm text-red-500">
                Call 117 or file a report immediately.
              </p>
            </div>
          </div>

          <a
            href="tel:117"
            className="bg-red-600 text-white font-bold px-5 py-3 rounded-2xl whitespace-nowrap w-full sm:w-auto text-center hover:bg-red-700 transition"
          >
            Call Now
          </a>
        </section>

      </div>
    </AppLayout>
  );
}